'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ThemeToggle } from './theme/ThemeToggle';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import {
  Menu,
  Settings,
  Home,
  Calendar,
  BookOpen,
  Target,
  BarChart3,
  GraduationCap,
  Search,
  X,
} from 'lucide-react';
import { SearchDataProvider } from './SearchDataProvider';
import { cn } from '../lib/utils';
import type { LucideIcon } from 'lucide-react';

type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  shortLabel?: string;
  badge?: string;
};

// App navigation (core features)
// Modules moved to second position for prominence
const appNavigation: NavItem[] = [
  { href: '/', label: 'Home', icon: Home, shortLabel: 'Home' },
  { href: '/start', label: 'Start Here', icon: Target, shortLabel: 'Start' },
  { href: '/ritual', label: 'Ritual', icon: Calendar, shortLabel: 'Ritual' },
  { href: '/modules', label: 'Modules', icon: GraduationCap, shortLabel: 'Modules' },
  { href: '/journal', label: 'Journal', icon: BookOpen, shortLabel: 'Journal' },
  { href: '/scenarios', label: 'Scenarios', icon: Target, shortLabel: 'Scenarios' },
  { href: '/review', label: 'Review', icon: BarChart3, shortLabel: 'Review' },
];

// Marketing navigation (moved to footer/dropdown)
const marketingNavigation = [
  { href: '/press', label: 'Press' },
  { href: '/download', label: 'Download' },
  { href: '/blog', label: 'Blog' },
];

export function SiteHeader() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Memoize active state check
  const isActive = useCallback(
    (href: string) => {
      if (href === '/') {
        return pathname === '/';
      }
      return pathname?.startsWith(href);
    },
    [pathname]
  );

  // Close search on escape key
  useEffect(() => {
    if (!searchOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [searchOpen]);

  // Close menu on escape key
  useEffect(() => {
    if (!menuOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  // Close search when navigating
  useEffect(() => {
    if (searchOpen) {
      setSearchOpen(false);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize navigation items with active states
  const navItems = useMemo(
    () =>
      appNavigation.map((item) => ({
        ...item,
        active: isActive(item.href),
      })),
    [isActive]
  );

  // Hide SiteHeader on routes that have their own navigation (AppLayout)
  const isAppRoute = useMemo(() => {
    if (!pathname) return false;
    const appRoutes = ['/ritual', '/journal', '/habits', '/review', '/settings', '/paywall'];
    return appRoutes.some((route) => pathname.startsWith(route));
  }, [pathname]);

  if (isAppRoute) return null;

  return (
    <header
      role="banner"
      className="sticky top-0 z-50 backdrop-blur-md supports-[backdrop-filter]:bg-background/90 border-b border-border/50 bg-background/95 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 gap-2 sm:gap-3 md:gap-4">
          {/* Logo - Enhanced prominence */}
          <Link
            href="/"
            aria-label="Sovereign Mind — Home"
            className="flex items-center gap-2 font-bold text-xl hover:text-primary transition-colors shrink-0 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-all group-focus-visible:ring-2 group-focus-visible:ring-ring">
              <span className="text-primary font-bold text-sm">SM</span>
            </div>
            <span className="hidden sm:inline font-semibold tracking-tight">Sovereign Mind</span>
          </Link>

          {/* Desktop Navigation - Enhanced spacing and visual hierarchy */}
          <nav
            aria-label="Primary navigation"
            className="hidden lg:flex items-center gap-1.5 flex-1 min-w-0 justify-center"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    'relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-lg whitespace-nowrap shrink-0 group',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    item.active
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  )}
                  aria-current={item.active ? 'page' : undefined}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform',
                      item.active ? 'scale-110' : 'group-hover:scale-105'
                    )}
                    aria-hidden="true"
                  />
                  <span className="hidden xl:inline">{item.label}</span>
                  <span className="xl:hidden">{item.shortLabel || item.label}</span>
                  {item.active && (
                    <span
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary-foreground/60"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions - Better organized */}
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            {/* Search - Desktop: full width, Tablet/Mobile: icon button */}
            <div className="hidden lg:block w-64 xl:w-80 shrink-0">
              <SearchDataProvider />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label={searchOpen ? 'Close search' : 'Open search'}
              aria-expanded={searchOpen}
              aria-controls="search-modal"
            >
              {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </Button>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Settings - More prominent */}
            <Link
              href="/settings"
              className={cn(
                'hidden md:flex items-center justify-center p-2 rounded-lg text-sm font-medium transition-all shrink-0',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isActive('/settings')
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              aria-label="Settings"
              title="Settings"
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
            </Link>

            {/* Mobile Menu */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
                  aria-expanded={menuOpen}
                  aria-controls="mobile-primary-nav"
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-80 sm:w-96 p-0 flex flex-col"
                onEscapeKeyDown={() => setMenuOpen(false)}
                onPointerDownOutside={() => setMenuOpen(false)}
              >
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="px-6 py-5 border-b border-border">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                        <span className="text-primary font-bold text-sm">SM</span>
                      </div>
                      <span className="font-semibold text-lg">Sovereign Mind</span>
                    </div>
                    {/* Search in mobile menu */}
                    <div className="lg:hidden">
                      <SearchDataProvider />
                    </div>
                  </div>

                  {/* Mobile Navigation */}
                  <div className="flex-1 overflow-y-auto px-4 py-6">
                    <nav
                      id="mobile-primary-nav"
                      className="space-y-1"
                      role="navigation"
                      aria-label="Mobile Primary Navigation"
                    >
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            className={cn(
                              'flex items-center gap-3 py-3 px-4 rounded-lg transition-all',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              item.active
                                ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                                : 'hover:bg-muted/50 text-foreground'
                            )}
                            aria-current={item.active ? 'page' : undefined}
                            onClick={() => setMenuOpen(false)}
                          >
                            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            <span className="font-medium">{item.label}</span>
                            {item.active && (
                              <span
                                className="ml-auto w-2 h-2 rounded-full bg-primary-foreground/60"
                                aria-hidden="true"
                              />
                            )}
                          </Link>
                        );
                      })}
                    </nav>

                    {/* Resources Section */}
                    <div className="mt-8 pt-6 border-t border-border">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-4">
                        Resources
                      </div>
                      <nav className="space-y-1" aria-label="Resources">
                        {marketingNavigation.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            className={cn(
                              'block py-2.5 px-4 rounded-lg transition-colors text-sm',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              isActive(item.href)
                                ? 'bg-muted font-medium text-foreground'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            )}
                            aria-current={isActive(item.href) ? 'page' : undefined}
                            onClick={() => setMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                        <Link
                          href="/settings"
                          className={cn(
                            'flex items-center gap-2 py-2.5 px-4 rounded-lg transition-colors text-sm',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isActive('/settings')
                              ? 'bg-muted font-medium text-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                          )}
                          onClick={() => setMenuOpen(false)}
                        >
                          <Settings className="h-4 w-4" aria-hidden="true" />
                          Settings
                        </Link>
                      </nav>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Search Modal for Tablet/Mobile - Improved with proper z-index */}
        {searchOpen && (
          <div
            className="lg:hidden fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm"
            onClick={() => setSearchOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchOpen(false);
              }
            }}
            aria-hidden="true"
          >
            <div
              id="search-modal"
              className="absolute top-16 left-0 right-0 bg-background border-b border-border shadow-lg p-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              <div className="max-w-2xl mx-auto">
                <SearchDataProvider />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
